import { motion } from 'framer-motion';
import { ArrowRight, Flame, Smartphone, Target } from 'lucide-react';
import { useState, useEffect } from 'react';

interface HeroProps {
    signupCount: number;
}

// Phone mockup component with real app screenshot
function PhoneMockup({ screenshot, alt }: { screenshot: string; alt: string }) {
    return (
        <div className="relative">
            {/* Phone frame */}
            <div className="relative bg-black rounded-[2.5rem] p-2 shadow-2xl shadow-black/50">
                {/* Dynamic Island */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-20" />
                {/* Screen */}
                <div className="relative rounded-[2rem] overflow-hidden bg-dark-900">
                    <img src={screenshot} alt={alt} className="w-full h-auto" />
                </div>
            </div>
        </div>
    );
}

const screenshots = [
    { src: '/app-feed.png', alt: 'DareUp Feed - Watch and discover dare videos' },
    { src: '/app-explore-dares.png', alt: 'DareUp Explore - Browse trending dares' },
    { src: '/app-discover.png', alt: 'DareUp Discover - Find creators and challenges' },
];

export default function Hero({ signupCount }: HeroProps) {
    const [currentScreenshot, setCurrentScreenshot] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentScreenshot((prev) => (prev + 1) % screenshots.length);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    const scrollToWaitlist = () => {
        document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section className="relative gradient-bg min-h-screen flex items-center overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-1/2 -right-1/4 w-[600px] h-[600px] bg-primary-500 rounded-full opacity-10 blur-[120px]" />
                <div className="absolute -bottom-1/2 -left-1/4 w-[600px] h-[600px] bg-primary-600 rounded-full opacity-10 blur-[120px]" />
                {/* Grid pattern overlay */}
                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,107,53,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,107,53,0.03)_1px,transparent_1px)] bg-[size:64px_64px]" />
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    {/* Left Column - Text Content */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center lg:text-left"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.2 }}
                            className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-primary-500/10 text-primary-400 rounded-full text-sm font-semibold border border-primary-500/20"
                        >
                            <Flame className="w-4 h-4" />
                            Launching Q1 2026
                        </motion.div>

                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 leading-[1.1]">
                            <span className="sr-only">DareUp - </span>
                            Stop Scrolling.
                            <br />
                            <span className="text-gradient">Start Creating.</span>
                        </h1>

                        <p className="text-lg sm:text-xl text-gray-400 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                            The app that turns creative challenges into viral content. Pick a dare, record your take,
                            and join a community of creators.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 mb-8 justify-center lg:justify-start">
                            <button
                                onClick={scrollToWaitlist}
                                className="group px-8 py-4 bg-primary-500 hover:bg-primary-600 text-white rounded-2xl font-semibold text-lg shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
                            >
                                Join the Waitlist
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>

                        {/* Platform badges */}
                        <div className="flex flex-wrap items-center gap-3 mb-8 justify-center lg:justify-start">
                            <div className="flex items-center gap-2 px-4 py-2.5 bg-dark-800/80 border border-dark-700 rounded-xl">
                                <Smartphone className="w-5 h-5 text-gray-300" />
                                <span className="text-sm font-medium text-gray-300">iOS & Android</span>
                            </div>
                        </div>

                        {signupCount > 0 && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4 }}
                                className="flex items-center gap-3 text-gray-400 justify-center lg:justify-start"
                            >
                                <div className="flex -space-x-2">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div
                                            key={i}
                                            className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 border-2 border-dark-900 flex items-center justify-center"
                                        >
                                            <Flame className="w-4 h-4 text-white" />
                                        </div>
                                    ))}
                                </div>
                                <span className="text-sm">
                                    <strong className="text-white">{signupCount.toLocaleString()}+</strong> creators
                                    waiting
                                </span>
                            </motion.div>
                        )}
                    </motion.div>

                    {/* Right Column - Phone Mockup with Real Screenshots */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="relative flex justify-center"
                    >
                        {/* Glow effect behind phone */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-primary-500/20 rounded-full blur-[100px]" />

                        {/* Main phone with screenshot carousel */}
                        <div className="relative w-64 sm:w-72">
                            <PhoneMockup
                                screenshot={screenshots[currentScreenshot].src}
                                alt={screenshots[currentScreenshot].alt}
                            />

                            {/* Screenshot indicator dots */}
                            <div className="flex justify-center gap-2 mt-4">
                                {screenshots.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setCurrentScreenshot(index)}
                                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                            index === currentScreenshot
                                                ? 'bg-primary-500 w-6'
                                                : 'bg-dark-700 hover:bg-dark-600'
                                        }`}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Floating dare cards */}
                        <motion.div
                            animate={{ y: [0, -8, 0] }}
                            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                            className="absolute -left-4 sm:left-0 top-16 bg-dark-800/90 backdrop-blur-sm border border-dark-700 rounded-2xl shadow-xl p-3 sm:p-4 w-40 sm:w-44"
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-6 h-6 rounded-lg bg-primary-500/20 flex items-center justify-center">
                                    <Flame className="w-3.5 h-3.5 text-primary-500" />
                                </div>
                                <span className="text-xs font-medium text-primary-400">Trending</span>
                            </div>
                            <div className="text-sm font-semibold text-white">Voice Impressions</div>
                            <div className="text-xs text-gray-500 mt-0.5">2 videos • Medium</div>
                        </motion.div>

                        <motion.div
                            animate={{ y: [0, 8, 0] }}
                            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
                            className="absolute -right-4 sm:right-0 bottom-24 bg-dark-800/90 backdrop-blur-sm border border-dark-700 rounded-2xl shadow-xl p-3 sm:p-4 w-40 sm:w-44"
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-6 h-6 rounded-lg bg-green-500/20 flex items-center justify-center">
                                    <Target className="w-3.5 h-3.5 text-green-500" />
                                </div>
                                <span className="text-xs font-medium text-green-400">Easy</span>
                            </div>
                            <div className="text-sm font-semibold text-white">Show Your Talent</div>
                            <div className="text-xs text-gray-500 mt-0.5">Perfect for beginners</div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            {/* Scroll indicator */}
            <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
            >
                <div className="w-6 h-10 border-2 border-dark-700 rounded-full flex justify-center">
                    <div className="w-1.5 h-3 bg-primary-500 rounded-full mt-2" />
                </div>
            </motion.div>
        </section>
    );
}
